// A book in the library: its cover, author, what it is, what it is to you, and its contents. Free
// works and works you own open each section in the reader (from the package, on the API). A paid work
// you don't own shows its contents only, never its text, with "Get it" when web purchases are open
// (Stripe, the API's web.purchases switch) and "Available in the Bible Standard app" when they're
// not. A work whose rights are pending says so calmly. Add it to My library, file it in collections,
// and continue where you left off: all synced.
//
// The book is the biggest thing on the page (iOS FS-297): it arrives from the shelf it was taken
// from (a shared-element morph), and beginning or continuing swings its cover open before the
// reader follows.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, type MouseEvent } from "react";
import { Button, LinkButton } from "../../../components/Button";
import { EmptyState } from "../../../components/Empty";
import { Menu } from "../../../components/Menu";
import { usePocketKeep } from "../../../components/pocket/Pocket";
import { Icon } from "../../../components/Icon";
import { OpeningBook } from "../../../components/library/OpeningBook";
import { SectionHeader } from "../../../components/SectionHeader";
import { Skeleton, SkeletonText } from "../../../components/Skeleton";
import ui from "../../../components/ui.module.css";
import { usePlans } from "../../../data/planQueries";
import { FRAME_ROLES, KIND_LABEL, toneOf, useAuthors, useToc, useWork, type Toc, type Work } from "../../../data/works";
import { ACCESS_LINE, accessState, UNAVAILABLE_NOTE, useCheckout, useEntitlements, useLibrarySource, type AccessState } from "../../../data/catalogueSource";
import { usePurchasesOpen } from "../../../data/queries";
import { morphName } from "../../../motion/shared";
import { cssOf } from "../../../motion/tokens";
import { usePosition, useChange } from "../../../reader/useMarks";
import { asShelfCollection, asShelfItem, shelfAdd, shelfFile, shelfRemove } from "../../../sync/domain";
import { useCollection } from "../../../sync/react";
import { useTitle } from "../../../lib/useTitle";
import s from "./books.module.css";

export const Route = createFileRoute("/_app/books/$id")({ component: BookPage });

/** Contents rows worth listing: headings and reader pages (not the paragraphs folded into a page). */
function contentsOf(toc: Toc) {
  const heads = new Set(toc.pages);
  const depth: number[] = [];
  const out: { i: number; uid: string | null; title: string; depth: number; frame: boolean }[] = [];
  toc.rows.forEach((r, i) => {
    depth[i] = r[3] >= 0 ? depth[r[3]]! + 1 : 0;
    if (r[0] ? heads.has(i) : toc.rows.some((x) => x[3] === i)) out.push({ i, uid: r[0], title: r[1], depth: depth[i]!, frame: FRAME_ROLES.has(r[2] ?? "") });
  });
  // A single untitled-by-content root (the volume's own title) adds nothing above the page title.
  const min = Math.min(...out.map((r) => r.depth));
  const top = out.filter((r) => r.depth === min);
  const lift = top.length === 1 && !top[0]!.uid ? 1 : 0;
  return out.filter((r) => r.depth >= min + lift).map((r) => ({ ...r, depth: r.depth - min - lift }));
}

const SHORT = 60;
/** How long the board takes to swing before the reader follows. */
/** The cover swings on iOS's `bookOpenSwing`; the reader follows once it lands. */
const SWING = cssOf("bookOpenSwing").duration;

function BookPage() {
  const { id } = Route.useParams();
  const { data: work, isPending } = useWork(id);
  const { data: authors } = useAuthors();
  const { data: toc } = useToc(work ? id : null);
  const { data: plans = [] } = usePlans();
  const { data: ents } = useEntitlements();
  const purchases = usePurchasesOpen();
  const source = useLibrarySource();
  const place = usePosition(id);
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const [opening, setOpening] = useState(false);
  useTitle(work?.title ?? "Library");
  const rows = useMemo(() => (toc ? contentsOf(toc) : []), [toc]);

  if (isPending)
    return (
      <div className={ui.page} aria-busy="true">
        <div className={s.hero}>
          <Skeleton shape="block" width={196} height={300} />
          <div className={s.heroText}>
            <Skeleton width="80%" height={36} />
            <SkeletonText lines={3} />
          </div>
        </div>
      </div>
    );
  if (!work)
    return (
      <div className={ui.page}>
        <EmptyState title="This isn’t in the library." body="It may have moved, or the link may be incomplete.">
          <LinkButton to="/library">Go to the library</LinkButton>
        </EmptyState>
      </div>
    );

  const state: AccessState = accessState(work.entry, ents, { purchases, webText: work.text });
  const readable = source.live ? state === "free" || state === "owned" : work.text;
  const people = work.authorIds.map((a) => authors?.find((x) => x.id === a)).filter((a) => !!a && !a.collection);
  const byline = authors?.find((x) => x.id === work.authorIds[0]);
  const shortList = rows.length > SHORT * 1.5 && !all;
  const visible = shortList ? rows.filter((r) => r.depth === 0).slice(0, SHORT) : rows;
  const firstPage = toc ? rows.find((r) => r.uid && !r.frame)?.uid : undefined;
  const resume = place?.anchor?.sectionUid && toc?.rows.some((r) => r[0] === place.anchor!.sectionUid) ? place.anchor.sectionUid : null;
  const start = resume ?? firstPage;
  const reading = plans.filter((p) => p.companionWork?.libraryId === work.id);

  /** Swing the cover open, then follow it into the reader. A modified click opens as the browser would. */
  const open = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!start || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    e.preventDefault();
    setOpening(true);
    window.setTimeout(() => void navigate({ to: "/books/$id/$section", params: { id: work.id, section: start } }), SWING);
  };

  const book = (
    <OpeningBook
      id={work.id}
      title={work.title}
      shortTitle={work.shortTitle}
      author={work.author}
      tone={toneOf(work, authors)}
      kind={work.kind}
      dimmed={!readable}
      open={opening}
      style={{ viewTransitionName: morphName("book", work.id) }}
    />
  );

  return (
    <div className={ui.page}>
      <div className={ui.pageHead}>
        <Link to="/library" className={ui.back} aria-label="Library">
          <Icon name="back" />
        </Link>
      </div>

      <header className={s.hero}>
        <div className={s.stage}>
          {readable && start ? (
            <Link to="/books/$id/$section" params={{ id: work.id, section: start }} className={s.bookLink} onClick={open} tabIndex={-1} aria-hidden="true">
              {book}
            </Link>
          ) : (
            book
          )}
          <span className={s.ledge} aria-hidden="true" />
        </div>
        <div className={s.heroText}>
          <h1 className={s.title} data-long={work.title.length > 44 || undefined}>
            {work.title}
          </h1>
          <p className={s.byline}>
            {people.length > 0 && people.length <= 3 ? (
              people.map((a, i) => (
                <span key={a!.id}>
                  {i > 0 && (i === people.length - 1 ? " and " : ", ")}
                  <Link to="/authors/$id" params={{ id: a!.id }}>
                    {a!.name}
                  </Link>
                </span>
              ))
            ) : byline ? (
              <Link to="/authors/$id" params={{ id: byline.id }}>
                {work.author}
              </Link>
            ) : (
              work.author
            )}
          </p>
          <p className={s.meta}>{[KIND_LABEL[work.kind] ?? work.kind, work.written, people.length > 3 ? `${people.length} writers` : null].filter(Boolean).join(" · ")}</p>
          <p className={state === "get" ? s.stateGet : s.state}>{ACCESS_LINE[state]}</p>
          {readable ? (
            <div className={s.actions}>
              {start && (
                <LinkButton to="/books/$id/$section" params={{ id: work.id, section: start }} variant="primary" size="lg" icon="read" onClick={open}>
                  {resume ? "Continue reading" : "Begin reading"}
                </LinkButton>
              )}
              <ShelfButton work={work} />
              <PocketMenu id={work.id} />
            </div>
          ) : (
            <Away work={work} state={state} />
          )}
        </div>
      </header>

      <p className={s.rights}>
        {[work.edition?.split(/ [—–-] /)[0], work.licence].filter(Boolean).join(". ")}
        {readable ? ". From the Bible Standard app’s own edition." : ""}
      </p>

      {readable && <Collections work={work} />}

      {readable && work.text && (
        <Link to="/plans/new" search={{ alongside: work.id }} className={s.alongside}>
          <Icon name="bible" size={20} />
          <span>
            <span className={s.alongsideTitle}>Read it alongside Scripture in a plan</span>
            <span className={s.meta}>A section beside each day’s chapters, alone or with friends.</span>
          </span>
          <Icon name="forward" size={18} />
        </Link>
      )}

      <section className={s.contents} aria-labelledby="contents-head">
        <SectionHeader id="contents-head" title="Contents" />
        {!toc ? (
          <div aria-busy="true" className={s.loading}>
            <SkeletonText lines={6} />
          </div>
        ) : (
          <ol className={s.toc}>
            {visible.map((r) => (
              <li key={r.i} className={r.depth > 0 ? s[`d${Math.min(r.depth, 3)}`] : undefined}>
                {r.uid && readable ? (
                  <Link to="/books/$id/$section" params={{ id: work.id, section: r.uid }}>
                    {r.title}
                  </Link>
                ) : (
                  <span className={r.uid ? s.ref : s.heading}>{r.title}</span>
                )}
              </li>
            ))}
          </ol>
        )}
        {rows.length > SHORT * 1.5 && (
          <Button variant="text" onClick={() => setAll(!all)}>
            {all ? "Show the main divisions" : `Show all ${rows.length} entries`}
          </Button>
        )}
      </section>

      {reading.length > 0 && (
        <section className={s.contents} aria-labelledby="plans-head">
          <SectionHeader id="plans-head" title="Plans that read it" />
          <ul className={s.toc}>
            {reading.map((p) => (
              <li key={p.id}>
                <Link to="/plans/about/$planId" params={{ planId: p.id }}>
                  {p.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** Why the text isn't here, and what to do: get it, read it in the app, or wait for its rights. */
function Away({ work, state }: { work: Work; state: AccessState }) {
  const checkout = useCheckout();
  if (state === "unavailable") return <p className={s.away}>{UNAVAILABLE_NOTE}</p>;
  if (state === "get") {
    const product = work.productIds[0];
    return (
      <div className={s.actions}>
        <Button variant="primary" size="lg" busy={checkout.isPending} disabled={checkout.isPending || !product} onClick={() => product && checkout.mutate(product.key)}>
          {checkout.isPending ? "Opening checkout…" : "Get it"}
        </Button>
        <p className={s.away}>Paid securely with Stripe. Once it’s yours, it opens here and in the app. Here you can see what’s in it.</p>
        {checkout.isError && (
          <p className={ui.error} role="alert">
            {checkout.error.message}
          </p>
        )}
      </div>
    );
  }
  if (state === "reference") return <p className={s.away}>{ACCESS_LINE.reference}. Its text isn’t published on the web yet.</p>;
  return <p className={s.away}>Here you can see what’s in it.</p>;
}

/** "Keep in Pocket" (iOS FS-200 R4, the book sheet's •••): the book stands in the Bible tile's pocket. */
function PocketMenu({ id }: { id: string }) {
  const keep = usePocketKeep(id);
  if (!keep) return null;
  return <Menu label="More for this book" items={[{ label: keep.label, icon: keep.icon, onSelect: keep.onSelect, disabled: keep.disabled }]} />;
}

/** Add to or remove from My library (a synced shelf item, one per work). */
function ShelfButton({ work }: { work: Work }) {
  const { data = [] } = useCollection("shelf");
  const change = useChange();
  const item = data.map(asShelfItem).find((i) => i?.libraryId === work.id && !i.hidden);
  return (
    <Button size="lg" icon={item ? "check" : "plus"} aria-pressed={!!item} onClick={async () => void change(item ? await shelfRemove(work.id) : await shelfAdd(work.id))}>
      {item ? "In My library" : "Add to My library"}
    </Button>
  );
}

/** File the work in the reader's own collections (shown once it's on the shelf and collections exist). */
function Collections({ work }: { work: Work }) {
  const { data = [] } = useCollection("shelf");
  const change = useChange();
  const item = data.map(asShelfItem).find((i) => i?.libraryId === work.id && !i.hidden);
  const collections = data.map(asShelfCollection).filter((c) => !!c);
  if (!item || !collections.length) return null;
  return (
    <fieldset className={s.collections}>
      <legend className={ui.label}>In your collections</legend>
      {collections.map((c) => (
        <label key={c!.id} className={s.collection}>
          <input
            type="checkbox"
            checked={item.collectionIds.includes(c!.id)}
            onChange={async (e) =>
              void change(await shelfFile(work.id, e.target.checked ? [...item.collectionIds, c!.id] : item.collectionIds.filter((x) => x !== c!.id)))
            }
          />
          {c!.name}
        </label>
      ))}
    </fieldset>
  );
}
